import type { Artist, CommissionArrangement, SaleLine } from '@/domain/types';

/**
 * The single commission engine (Prompt E2). Given an artist's arrangement and a
 * day's sale lines, it works out the envelope the artist is owed and the house
 * share. Merch/aftercare lines are excluded — the studio keeps those whole.
 *
 * READ, DON'T RECOMPUTE: E3's calculations screen reads the payout numbers this
 * engine produced (via the stored PayoutRecord), it never re-runs the maths its
 * own way. This module is the one place the arrangement rules live.
 */

/** A day's commission split for one artist. */
export interface CommissionBreakdown {
  arrangement: CommissionArrangement;
  /** Commissionable session takings (merch excluded). */
  grossAud: number;
  /** Merch/aftercare taken alongside, never part of the payout. */
  merchAud: number;
  /** The studio's share of the commissionable gross. */
  houseShareAud: number;
  /** The artist's envelope for the day. */
  payoutAud: number;
  /** Number of commissionable sessions counted. */
  sessionCount: number;
}

/**
 * Resolve the arrangement to apply. An explicit `commissionArrangement` on the
 * artist wins (the per-artist override slot); otherwise it is the percentage
 * default derived from `commissionRate`.
 */
export function arrangementForArtist(
  artist: Pick<Artist, 'commissionRate' | 'commissionArrangement'>,
): CommissionArrangement {
  return artist.commissionArrangement ?? { kind: 'percentage', artistShare: artist.commissionRate };
}

/**
 * Compute the day's payout for one arrangement over its sale lines. Payout is
 * rounded to whole dollars — cash envelopes are counted in notes and coins, not
 * cents. `// ASSUMPTION:` whole-dollar rounding of the envelope is a demo choice;
 * the client hasn't specified rounding treatment.
 */
export function computePayout(
  arrangement: CommissionArrangement,
  lines: readonly SaleLine[],
): CommissionBreakdown {
  const sessionLines = lines.filter((l) => !l.isMerch);
  const grossAud = sessionLines.reduce((sum, l) => sum + l.amountAud, 0);
  const merchAud = lines.filter((l) => l.isMerch).reduce((sum, l) => sum + l.amountAud, 0);

  let payoutAud: number;
  switch (arrangement.kind) {
    case 'percentage':
      payoutAud = Math.round(grossAud * arrangement.artistShare);
      break;
    case 'owner-artist':
      payoutAud = grossAud;
      break;
    case 'flat-day-fee':
      // Flat fee is owed only if the artist actually worked a session today.
      payoutAud = sessionLines.length > 0 ? arrangement.feeAud : 0;
      break;
  }

  return {
    arrangement,
    grossAud,
    merchAud,
    houseShareAud: grossAud - payoutAud,
    payoutAud,
    sessionCount: sessionLines.length,
  };
}

/** Convenience: resolve the artist's arrangement and compute in one call. */
export function computePayoutForArtist(
  artist: Pick<Artist, 'commissionRate' | 'commissionArrangement'>,
  lines: readonly SaleLine[],
): CommissionBreakdown {
  return computePayout(arrangementForArtist(artist), lines);
}
